// Chemin: C:\gestion-copro\src\pages\public\InscriptionPage.tsx
// Page d'inscription pour les coproprietaires - ORION PILOT
// Session 8 : formulaire complet avec GSM, etage, lots + icone oeil mot de passe

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  InscriptionFormData, 
  Lot, 
  LotType,
  LOT_TYPE_LABELS,
  ETAGE_LABELS 
} from '../../types/user.types';
import { registerCoproprietaire, validateGSM } from '../../services/inscription.service';

const InscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<InscriptionFormData>({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
    gsm: '',
    etage: 0,
    lots: [{ type: 'appartement', numero: '' }]
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Etats pour afficher/masquer les mots de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Gestion des champs simples
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'etage' ? parseInt(value) : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Gestion des lots
  const handleLotChange = (index: number, field: keyof Lot, value: string) => {
    const newLots = [...formData.lots];
    newLots[index] = { ...newLots[index], [field]: value };
    setFormData(prev => ({ ...prev, lots: newLots }));
  };

  const addLot = () => {
    if (formData.lots.length < 5) {
      setFormData(prev => ({
        ...prev,
        lots: [...prev.lots, { type: 'appartement', numero: '' }]
      }));
    }
  };

  const removeLot = (index: number) => {
    if (formData.lots.length > 1) {
      setFormData(prev => ({
        ...prev,
        lots: prev.lots.filter((_, i) => i !== index)
      }));
    }
  };

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nom.trim() || formData.nom.trim().length < 2) {
      newErrors.nom = 'Nom requis (min 2 caracteres)';
    }
    if (!formData.prenom.trim() || formData.prenom.trim().length < 2) {
      newErrors.prenom = 'Prenom requis (min 2 caracteres)';
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email valide requis';
    }
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Mot de passe requis (min 6 caracteres)';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    if (!validateGSM(formData.gsm)) {
      newErrors.gsm = 'Numero GSM invalide (format: 06xxxxxxxx ou 07xxxxxxxx)';
    }
    
    // Verifier les lots
    const hasEmptyLot = formData.lots.some(lot => !lot.numero.trim());
    if (hasEmptyLot) {
      newErrors.lots = 'Tous les lots doivent avoir un numero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    setLoading(true);

    try {
      const result = await registerCoproprietaire(
        formData.email,
        formData.password,
        formData.nom.trim(),
        formData.prenom.trim(),
        formData.gsm,
        formData.etage,
        formData.lots
      );

      if (result.success) {
        navigate('/inscription-confirmation');
      } else {
        setError(result.error || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      setError('Une erreur inattendue est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Composant icone oeil
  const EyeIcon = ({ open }: { open: boolean }) => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      strokeWidth={1.5} 
      stroke="currentColor" 
      className="w-5 h-5"
    >
      {open ? (
        // Oeil ouvert
        <>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </>
      ) : (
        // Oeil barre
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
      )}
    </svg>
  );

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* En-tete */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-900 mb-2">
            Inscription Coproprietaire
          </h1>
          <p className="text-gray-600">Residence L'ORION - Merlimont</p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6 space-y-4">
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Nom / Prenom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.nom ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prenom *</label>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.prenom ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Mot de passe avec icone oeil */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          {/* Confirmer mot de passe avec icone oeil */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer mot de passe *</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <EyeIcon open={showConfirmPassword} />
              </button>
            </div>
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
          </div>

          {/* GSM */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telephone mobile *</label>
            <input
              type="tel"
              name="gsm"
              value={formData.gsm}
              onChange={handleChange}
              placeholder="06xxxxxxxx"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.gsm ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.gsm && <p className="text-red-500 text-xs mt-1">{errors.gsm}</p>}
          </div>

          {/* Etage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Etage *</label>
            <select
              name="etage"
              value={formData.etage}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(ETAGE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Lots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vos lots *</label>
            {errors.lots && <p className="text-red-500 text-xs mb-2">{errors.lots}</p>}
            
            {formData.lots.map((lot, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <select
                  value={lot.type}
                  onChange={(e) => handleLotChange(index, 'type', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {Object.entries(LOT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={lot.numero}
                  onChange={(e) => handleLotChange(index, 'numero', e.target.value)}
                  placeholder="N°"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                />
                {formData.lots.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLot(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    X
                  </button>
                )}
              </div>
            ))}
            
            {formData.lots.length < 5 && (
              <button
                type="button"
                onClick={addLot}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Ajouter un lot
              </button>
            )}
          </div>

          {/* Bouton */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold text-white ${
              loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Inscription...' : 'S\'inscrire'}
          </button>

          {/* Lien connexion */}
          <p className="text-center text-sm text-gray-600">
            Deja inscrit ?{' '}
            <a href="/login" className="text-blue-600 hover:underline">Se connecter</a>
          </p>
        </form>
      </div>
    </div>
  );
};

export default InscriptionPage;